import React from 'react';
import {
    Box,
    Typography,
    IconButton,
    Paper,
    Divider,
    Button,
    List,
    ListItem,
    ListItemText,
    Chip,
    Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TranslateIcon from '@mui/icons-material/Translate';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import HistoryIcon from '@mui/icons-material/History';

const SectionHeader = ({ icon, title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 2 }}>
        {icon}
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
            {title}
        </Typography>
    </Box>
);

const SmartSuggestions = ({
    isOpen,
    onClose,
    focusedSegment,
    originalText,
    suggestions = {
        memory: [],
        glossary: [],
        ai: null,
        history: []
    },
    onInsert,
    onRevealAI,
    isGeneratingAI = false
}) => {
    if (!isOpen) return null;

    return (
        <Paper
            elevation={0}
            sx={{
                width: '100%',
                height: '100%',
                borderLeft: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#fcfcfc',
                position: 'sticky',
                top: '80px', // Below the header
                maxHeight: 'calc(100vh - 100px)',
                overflowY: 'auto'
            }}
        >
            {/* Header */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700 }}>Smart Suggestions</Typography>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ p: 2 }}>
                {focusedSegment === null ? (
                    <Box sx={{ textAlign: 'center', py: 8, color: 'text.disabled' }}>
                        <Typography variant="body2">Click a segment to see suggestions</Typography>
                    </Box>
                ) : (
                    <>
                        {/* Memory Matches */}
                        <SectionHeader icon={<TranslateIcon sx={{ fontSize: 18, color: '#1976d2' }} />} title="Memory Matches" />
                        {suggestions.memory && suggestions.memory.length > 0 ? (
                            suggestions.memory.map((match, idx) => (
                                <Box key={idx} sx={{ p: 1.5, mb: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="caption" fontWeight={700} color="warning.main">
                                            {match.confidence}% Match
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ mb: 1, fontSize: '0.85rem' }}>
                                        {match.translatedText}
                                    </Typography>
                                    <Button size="small" variant="outlined" fullWidth onClick={() => onInsert(match.translatedText)}>
                                        Insert
                                    </Button>
                                </Box>
                            ))
                        ) : (
                            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', display: 'block', mb: 1 }}>No memory matches found</Typography>
                        )}

                        <Divider sx={{ my: 1 }} />

                        {/* Glossary Matches */}
                        <SectionHeader icon={<LibraryBooksIcon sx={{ fontSize: 18, color: '#2e7d32' }} />} title="Glossary Matches" />
                        {suggestions.glossary && suggestions.glossary.length > 0 ? (
                            <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider', mb: 1 }}>
                                {suggestions.glossary.map((term, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: idx === suggestions.glossary.length - 1 ? 0 : 1 }}>
                                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>{term.source}</Typography>
                                        <Typography variant="body2" color="text.secondary">→</Typography>
                                        <Typography variant="body2" fontWeight={600} sx={{ color: 'primary.main', fontSize: '0.85rem' }}>{term.target}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', display: 'block', mb: 1 }}>No glossary matches in this segment</Typography>
                        )}

                        <Divider sx={{ my: 1 }} />

                        {/* AI Suggestion */}
                        <SectionHeader icon={<AutoAwesomeIcon sx={{ fontSize: 18, color: '#9c27b0' }} />} title="AI Suggestion" />
                        {suggestions.ai ? (
                            <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider', mb: 1 }}>
                                <Typography variant="body2" sx={{ mb: 1.5, fontSize: '0.85rem', lineHeight: 1.5 }}>
                                    {suggestions.ai}
                                </Typography>
                                <Button size="small" variant="contained" fullWidth disableElevation onClick={() => onInsert(suggestions.ai)}>
                                    Insert
                                </Button>
                            </Box>
                        ) : (
                            <Box sx={{ p: 1.5, bgcolor: '#f3f7ff', borderRadius: 1, border: '1px dashed', borderColor: 'primary.light', textAlign: 'center' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
                                    AI suggestions consume credits
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    onClick={onRevealAI}
                                    disabled={isGeneratingAI}
                                    startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                                >
                                    {isGeneratingAI ? 'Generating...' : 'Reveal Suggestion'}
                                </Button>
                            </Box>
                        )}

                        <Divider sx={{ my: 1 }} />

                        {/* Used Earlier */}
                        <SectionHeader icon={<HistoryIcon sx={{ fontSize: 18, color: '#757575' }} />} title="Used Earlier in Project" />
                        {suggestions.history && suggestions.history.length > 0 ? (
                            suggestions.history.map((item, idx) => (
                                <Box key={idx} sx={{ p: 1, mb: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem', mb: 0.5, fontStyle: 'italic' }}>
                                        {item.source}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                                        {item.target}
                                    </Typography>
                                    <Button size="small" sx={{ mt: 0.5, minWidth: 0, p: 0 }} onClick={() => onInsert(item.target)}>
                                        Insert
                                    </Button>
                                </Box>
                            ))
                        ) : (
                            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', display: 'block', mb: 1 }}>No prior matches</Typography>
                        )}
                    </>
                )}
            </Box>
        </Paper>
    );
};

export default SmartSuggestions;
